import type { SearchResultJob, UrlLocation } from '@seek/chalice-types';
import type React from 'react';
import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'src/store/react';
import striptags from 'striptags';
import type { SolMetadata } from '@seek/sol-js';
import { useSplitViewAccessibilityLinks } from 'src/components/SearchResultPage/SplitView/AccessabilityLinks/AccessabilityLinks';
import { useIsSplitViewEnabled } from 'src/store/featureFlags/hooks';
import SolImpressionTracker from 'src/modules/solTracker/SolImpressionTracker';
import { useEnrichedLocation } from 'src/modules/enriched-location';
import {
  useIsJobExternal,
  useQualifiedLinkParams,
} from 'src/modules/qualified-location';
import { setSelectedJobIdAction } from 'src/store/results/results';
import {
  selectAuthenticated,
  selectFeatureFlag,
  selectLocation,
  selectZoneFeatures,
} from 'src/store/selectors';

import { persistJobUrl } from '../../SplitView/helpers';

import useJDPNavLinkProps from './hooks/useJDPNavLinkProps';
import useJobViewModel from './hooks/useJobViewModel';
import useSalaryDisplayLabel from './hooks/useSalaryDisplayLabel';
import { useClassificationLinks } from './hooks/useClassificationLinks.ts';
import { useCompanyLink } from './hooks/useCompanyLink.ts';
import { useLocationLinks } from './hooks/useLocationLinks.ts';
import { useLinkProps } from '../../../NavLink/NavLink.tsx';
import type { JobActionOrigin } from '@seek/seek-jobs-analytics';
import { jobMetaClick } from 'src/store/search/search.ts';
import { useJobLinkOnClickCallback } from './hooks/useJobLinkOnClickCallback.ts';
import { useSaveJobSERP } from '../../../../hooks/useSaveJob/useSaveJobSERP.ts';
import { highlightFromHTML, JobCard } from '@seek/job-card';
import { useResponsiveValue } from 'braid-design-system';

interface JobListItemProps {
  isSelected?: boolean;
  job: SearchResultJob;
  setCurrentJob?: (job: SearchResultJob) => void;
}

const jobUrlWithCardOrigin = ({
  location,
  origin,
}: {
  location: UrlLocation;
  origin: JobActionOrigin;
}) => ({
  ...location,
  query: { ...location.query, origin },
});

function useJobDetailPageLink(
  job: SearchResultJob,
  setCurrentJob?: (job: SearchResultJob) => void,
) {
  const isSplitView = useIsSplitViewEnabled();
  const dispatch = useDispatch();
  const currentLocation = useSelector(selectLocation);

  const { focusReturnToSearchLink } = useSplitViewAccessibilityLinks();
  const getQualifiedLinkParams = useQualifiedLinkParams({
    shouldUseUrlResolver: true,
  });
  const { location, onBeforeNavigation } = useJDPNavLinkProps(job);
  const isAboveTablet = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true,
  });
  const isSplitViewDisplayed = isSplitView && isAboveTablet;

  // This will make sure the URL that push to `history` is the same as the URL on the Job Card's Title
  const jdpEnrichedLocation = useEnrichedLocation({ location });
  const handleClick = (selectedJob: SearchResultJob) =>
    isSplitViewDisplayed
      ? (e: React.MouseEvent) => {
          // setCurrentJob is defined only in split view
          if (setCurrentJob) {
            e.preventDefault();

            setCurrentJob(selectedJob);

            const jobId = selectedJob.id.toString();
            // mark link visited
            history.replaceState(
              {},
              '',
              jdpEnrichedLocation?.href || location.href,
            );
            // persist Job ID for desktop split view
            persistJobUrl({
              currentLocation,
              job,
            });
            dispatch(setSelectedJobIdAction(jobId));
            focusReturnToSearchLink();
          }
        }
      : undefined;
  const onClick = handleClick(job);

  const getLinkProps = useLinkProps({ getQualifiedLinkParams });
  const jobDetailPageLink = {
    ...getLinkProps(jdpEnrichedLocation),
    onBeforeNavigation,
  };

  const jobDetailPageJobCardOriginLink = {
    ...getLinkProps(
      useEnrichedLocation({
        location: jobUrlWithCardOrigin({ location, origin: 'jobCard' }),
      }),
    ),
  };

  const jobDetailPageCardTitleOriginLink = {
    ...getLinkProps(
      useEnrichedLocation({
        location: jobUrlWithCardOrigin({ location, origin: 'cardTitle' }),
      }),
    ),
  };

  return {
    onClick,
    jobLinkOnClickCallback: useJobLinkOnClickCallback({
      jobDetailPageLink,
      onClick,
    }),
    jobDetailPageLink,
    jobDetailPageJobCardOriginLink,
    jobDetailPageCardTitleOriginLink,
  };
}

const JobListItem = ({ isSelected, job, setCurrentJob }: JobListItemProps) => {
  const isSplitView = useIsSplitViewEnabled();
  const isAuthenticated = useSelector(selectAuthenticated);
  const { SHOW_JOBCARD_TEASER_IN_SPLITVIEW } = useSelector(selectZoneFeatures);
  const shouldOpenApplyInNewTab = useSelector(
    selectFeatureFlag('shouldOpenApplyInNewTab'),
  );

  const {
    bulletPoints,
    classification,
    currencyLabel,
    id,
    isPremium,
    joraImpressionTrackingUrl,
    listingDate,
    listingDateDescription,
    locationMatch,
    salary,
    srcLogo,
    teaser,
    title,
    unifiedListingDate,
    unifiedListingDateDescription,
    unifiedLocation,
    workType,
  } = useJobViewModel(job);

  const isJobExternal = useIsJobExternal(job.jobLocation?.countryCode);
  const salaryDisplayLabel = useSalaryDisplayLabel({
    salary,
    currencyLabel,
  });

  const {
    jobLinkOnClickCallback,
    jobDetailPageLink,
    jobDetailPageJobCardOriginLink,
    jobDetailPageCardTitleOriginLink,
    onClick,
  } = useJobDetailPageLink(job, setCurrentJob);

  const dispatch = useDispatch();
  const jobMetaLinkOnClickCallback = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const { type } = e.currentTarget.dataset;
      if (type) {
        dispatch(jobMetaClick(type as Parameters<typeof jobMetaClick>[0]));
      }
    },
    [dispatch],
  );

  const saveJobSERP = useSaveJobSERP({
    jobId: String(job.id),
    jobTracking: job?.tracking,
  });

  return (
    <JobCard
      aria-label={striptags(title)}
      bulletPoints={bulletPoints}
      companyLink={useCompanyLink({ job })}
      classificationLinks={useClassificationLinks({ classification })}
      id={id}
      isAuthenticated={isAuthenticated}
      isPremium={isPremium}
      isSelected={isSelected}
      isShowJobCardTeaserInSplitView={SHOW_JOBCARD_TEASER_IN_SPLITVIEW}
      jobDetailPageLink={jobDetailPageLink}
      jobDetailPageJobCardOriginLink={jobDetailPageJobCardOriginLink}
      jobDetailPageCardTitleOriginLink={jobDetailPageCardTitleOriginLink}
      jobTitle={highlightFromHTML({ html: title })}
      jobLinkOnClick={jobLinkOnClickCallback}
      jobMetaLinkOnClick={jobMetaLinkOnClickCallback}
      joraImpressionTrackingUrl={joraImpressionTrackingUrl}
      listingDate={unifiedListingDate ?? listingDate}
      listingDateDescription={
        unifiedListingDateDescription ?? listingDateDescription
      }
      locationLinks={useLocationLinks({ isJobExternal, unifiedLocation })}
      locationMatch={locationMatch}
      onClick={onClick}
      shouldAddFocusEffect={isSplitView}
      shouldAddHoverEffect={isSplitView && !isSelected}
      shouldOpenApplyInNewTab={shouldOpenApplyInNewTab}
      srcLogo={srcLogo}
      salaryDisplayLabel={salaryDisplayLabel}
      saveJob={saveJobSERP}
      tags={job.tags}
      teaser={teaser}
      workType={workType}
    />
  );
};

const withSolImpressionTracker = ({
  isSelected,
  job,
  setCurrentJob,
  solMetadata,
}: JobListItemProps & { solMetadata: SolMetadata }) => (
  <SolImpressionTracker jobId={job.id} solMetadata={solMetadata}>
    <JobListItem
      isSelected={isSelected}
      job={job}
      setCurrentJob={setCurrentJob}
    />
  </SolImpressionTracker>
);
export default memo(withSolImpressionTracker);
